import { Temporal } from "@js-temporal/polyfill";

export type SearchParamParser<T> = (values: string[]) => T | undefined;

const getSingleValue = (value: string[]): string | undefined => {
  const [head, ...tail] = value;

  // NOTE: there must be only one parameter with the same name
  // in the search string if this parameter is not an array
  if (tail.length > 0) {
    return undefined;
  }
  return head;
};

export const parseStringSearchParam: SearchParamParser<string> = getSingleValue;

/**
 * ```
 * | input   | parser result |
 * |—————————|———————————————|
 * | 'false' | false         |
 * | 'true'  | true          |
 * | 'foo'   | undefined     |
 * | ''      | undefined     |
 * | null    | undefined     |
 * ```
 */
export const parseBooleanSearchParam: SearchParamParser<boolean> = (
  values: string[],
) => {
  const value = getSingleValue(values);

  switch (value) {
    case "true":
      return true;
    case "false":
      return false;
    default:
      return undefined;
  }
};

/**
 * ```
 * | input   | parser result |
 * |—————————|———————————————|
 * | '42'    | 42            |
 * | '42.'   | 42            |
 * | '.42'   | 0.42          |
 * | '3.14'  | 3.14          |
 * | '1E-2'  | 0.01          |
 * | '3.14a' | undefined     |
 * | 'foo'   | undefined     |
 * | ''      | undefined     |
 * | null    | undefined     |
 * ```
 */
export const parseNumericSearchParam: SearchParamParser<number> = (
  values: string[],
) => {
  const value = getSingleValue(values);

  if (!value) {
    return undefined;
  }

  const parsed = Number(value);

  if (Number.isNaN(parsed)) {
    return undefined;
  }
  return parsed;
};

/**
 * ```
 * | input  | parser result |
 * |————————|———————————————|
 * | '42'   | 42            |
 * | '42.'  | 42            |
 * | '.42'  | 0.42          |
 * | '3.14' | undefined     |
 * | 'foo'  | undefined     |
 * | ''     | undefined     |
 * | null   | undefined     |
 * ```
 */
export const parseIntegerSearchParam: SearchParamParser<number> = (
  values: string[],
) => {
  const parsed = parseNumericSearchParam(values);
  if (parsed === undefined || !Number.isInteger(parsed)) {
    return undefined;
  }
  return parsed;
};

/**
 * valid input for date parser is ISO8601 string
 * https://en.wikipedia.org/wiki/ISO_8601
 */
export const parseDateSearchParam: SearchParamParser<Date> = (
  values: string[],
) => {
  const value = getSingleValue(values);

  if (!value) {
    return undefined;
  }

  const parsed = new Date(value);

  if (!isNaN(parsed.getTime())) {
    return parsed;
  }
  return undefined;
};

/**
 * valid format for arrays in URL search params:
 * ?foo=bar&foo=baz&foo=quux
 */
export const parseArraySearchParam =
  <T>(parser: SearchParamParser<T>): SearchParamParser<T[]> =>
  (values: string[]) => {
    if (values.length === 0) {
      return undefined;
    }

    const parsed = values.map((value) => parser([value]));

    if (parsed.some((item) => item === undefined)) {
      return undefined;
    }

    return parsed as T[];
  };

/**
 * valid input for plain date parser is ISO8601 string
 * https://en.wikipedia.org/wiki/ISO_8601
 * https://tc39.es/proposal-temporal/docs/plaindate.html#static-methods
 */
export const parsePlainTimeSearchParam: SearchParamParser<
  Temporal.PlainTime
> = (value: string[]) => {
  const v = value[0] ?? "";
  return v ? Temporal.PlainTime.from(v) : undefined;
};

/**
 * valid input for plain date parser is ISO8601 string
 * https://en.wikipedia.org/wiki/ISO_8601
 * https://tc39.es/proposal-temporal/docs/plaindate.html#static-methods
 */
export const parsePlainDateSearchParam: SearchParamParser<
  Temporal.PlainDate
> = (value: string[]) => {
  const v = value[0] ?? "";
  return v ? Temporal.PlainDate.from(v) : undefined;
};

/**
 * valid input for plain date time parser is ISO8601 string
 * https://en.wikipedia.org/wiki/ISO_8601
 * https://tc39.es/proposal-temporal/docs/plaindate.html#static-methods
 */
export const parsePlainDateTimeSearchParam: SearchParamParser<
  Temporal.PlainDateTime
> = (value: string[]) => {
  const v = value[0] ?? "";
  return v ? Temporal.PlainDateTime.from(v) : undefined;
};

/**
 * valid input for zoned date time parser is ISO8601 string
 * https://en.wikipedia.org/wiki/ISO_8601
 * https://tc39.es/proposal-temporal/docs/plaindate.html#static-methods
 */
export const parseZonedDateTimeSearchParam: SearchParamParser<
  Temporal.ZonedDateTime
> = (value: string[]) => {
  const v = value[0] ?? "";

  try {
    return v ? Temporal.ZonedDateTime.from(v) : undefined;
  } catch (e) {
    return v
      ? Temporal.ZonedDateTime.from(
          Temporal.PlainDateTime.from(v).toZonedDateTime(
            Temporal.TimeZone.from(Temporal.Now.timeZoneId()),
          ),
        )
      : undefined;
  }
};
